import {LEAD_WIZARD_GROUP_TYPES} from "./wizardLogic";

/**
 * @param dbFeature {dbFeatureInterface}
 * @param wizardGroup {wizardGroupInterface}
 * @returns {boolean}
 */
export const featureIsExcluded = (dbFeature, wizardGroup) => {
  const {sku, featureDefinitionID, customDisplayName, matchingName} = dbFeature;
  if (wizardGroup.useIncluded && wizardGroup.featuresIncluded) {
    return !wizardGroup.featuresIncluded.some(includedFeature => {
      return (includedFeature.sharedFeatureName && (includedFeature.sharedFeatureName === matchingName))
        || (includedFeature.featureDefinitionId === featureDefinitionID)
        || (!includedFeature.sharedFeatureName && (includedFeature.name === customDisplayName))
        || (includedFeature.sku && sku && (includedFeature.sku === sku))
    })
  }
  return false
}

/**
 * returns current wizard step section items
 * @param features {featureInterface}
 * @param wizardGroup {wizardGroupInterface}
 * @returns {array}
 */
export const getFilteredNavItems = (features, wizardGroup) => {
  return features.filter(feature => !featureIsExcluded(feature, wizardGroup))
};

/**
 * returns current wizard step section items
 * @param wizardActiveItem {wizardItemInterface}
 * @param wizardActiveStep {number}
 * @param wizardSelectedValues {object}
 * @param prevStepGroups {[wizardGroupInterface]}
 * @param additionalWizardValuesToCheck {object}
 * @returns {array}
 */
export const getCurrentGroups = (wizardActiveItem,
                                 wizardActiveStep,
                                 wizardSelectedValues,
                                 prevStepGroups = [],
                                 additionalWizardValuesToCheck= null) => {
  if (!wizardActiveItem || wizardActiveStep < 0) {
    return []
  }
  const stepStr = "" + wizardActiveStep;

  if (wizardActiveStep === 0) {
    return getCurrentGroupsFromSource(stepStr, wizardActiveItem)
  }

  const selectedPrevStep = wizardSelectedValues[wizardActiveStep - 1];
  if (!selectedPrevStep || !selectedPrevStep.wizardGroup) {
    if (prevStepGroups.length && prevStepGroups[0].id) {
      if (additionalWizardValuesToCheck) { // here we check also previously selected values to make steps similar to prev choices
        let foundPreviouslySelectedGroup = null;
        if (prevStepGroups.every(wg => wg.nodeType === "custom")) {
          Object.keys(additionalWizardValuesToCheck).forEach(key => {
            if ((key > Object.keys(wizardSelectedValues).length)
              && !additionalWizardValuesToCheck[key].partId
              && additionalWizardValuesToCheck[key].wizardGroup) {
              prevStepGroups.forEach(wg => {
                if (wg.id === additionalWizardValuesToCheck[key].wizardGroup.id) {
                  foundPreviouslySelectedGroup = wg;
                }
              });
            }
          });
        } else {
          // here we can add simple feature group, but no need for now
        }
        if (foundPreviouslySelectedGroup) {
          return getCurrentGroupsFromSource(foundPreviouslySelectedGroup.id, wizardActiveItem)
        }
      }
      return getCurrentGroupsFromSource(prevStepGroups[0].id, wizardActiveItem)
    }
    // console.log("Previous step is not selected! Check selected values --> ", wizardSelectedValues);
    return []
  }

  if (selectedPrevStep && selectedPrevStep.wizardGroup && selectedPrevStep.wizardGroup.mergedGroups && selectedPrevStep.selectedFeature) {
    const wizardGroupWithSelectedFeature = selectedPrevStep.wizardGroup.mergedGroups.find(wizGroup => {
      return !featureIsExcluded(selectedPrevStep.selectedFeature, wizGroup)
    });
    if (wizardGroupWithSelectedFeature) {
      return getCurrentGroupsFromSource(wizardGroupWithSelectedFeature.id, wizardActiveItem);
    }
  }

  // const prevStepStr = "" + (wizardActiveStep - 1);
  // const prevGroups = getCurrentGroupsFromSource(prevStepStr, wizardActiveItem);
  // if (prevGroups.length > 1 && wizardActiveStep > 0) {
  //   return [selectedPrevStep.wizardGroup]
  // }

  return getCurrentGroupsFromSource(selectedPrevStep.wizardGroup.id, wizardActiveItem);
}

/**
 * returns current wizard step section items
 * @param source {string}
 * @param wizardActiveItem {wizardItemInterface}
 * @returns array {[wizardGroupInterface]}
 */
export const getCurrentGroupsFromSource = (source, wizardActiveItem) => {
  if (!wizardActiveItem) {
    return []
  }
  const siblingGroups = wizardActiveItem.wizardGroups.filter(wizGroup => {
    return wizardActiveItem.wizardEdges.some(wizEdge => wizEdge.source === source && wizEdge.target === wizGroup.id && wizGroup.enabled)
  });
  // console.log("siblingGroups", siblingGroups);
  const areSameSiblingGroups = siblingGroups.length > 1 && siblingGroups.every(siblingGroup => {
    return (siblingGroup.nodeType === LEAD_WIZARD_GROUP_TYPES.group) && (siblingGroup.groupName === siblingGroups[0].groupName)
  });
  if (areSameSiblingGroups) {
    // console.log("areSameSiblingGroups", areSameSiblingGroups);

    if (siblingGroups.every(wizardGroup => wizardGroup.useIncluded && wizardGroup.featuresIncluded)) { // new logic
      const featuresIncluded = siblingGroups.reduce((total, wizardGroup) => {
        wizardGroup.featuresIncluded.forEach(included => {
          if (!total.some(exclObj => (exclObj.featureDefinitionId === included.featureDefinitionId))) {
            total.push(included)
          }
        })
        return total
      }, []);
      const newGroup = Object.assign({}, siblingGroups[0], {
        mergedGroups: siblingGroups,
        featuresExcluded: [], // not sure if we need it
        featuresIncluded
      })
      return [newGroup]
    }

    const allFeaturesExcluded = siblingGroups.reduce((total, wizardGroup) => {
      wizardGroup.featuresExcluded.forEach(excluded => {
        if (!excluded) {
          return
        }
        if (typeof excluded === "object") { // new logic for excluded
          if (!total.some(exclObj => (typeof exclObj === "object") && (exclObj.featureDefinitionId === excluded.featureDefinitionId))) {
            total.push(excluded)
          }
        } else if (!total.includes(excluded)) {
          total.push(excluded)
        }
      })
      return total
    }, []);
    const featuresExcluded = allFeaturesExcluded.filter(exclFeature => {
      return siblingGroups.every(siblingGroup => {
        if (typeof exclFeature === "object") {
          return siblingGroup.featuresExcluded.some(exclObj => (typeof exclObj === "object") && (exclObj.featureDefinitionId === exclFeature.featureDefinitionId))
        }
        return siblingGroup.featuresExcluded.includes(exclFeature)
      })
    })
    const newGroup = Object.assign({}, siblingGroups[0], {
      mergedGroups: siblingGroups,
      featuresExcluded
    })
    return [newGroup]
  }
  return siblingGroups
}

/**
 * returns current wizard step section items
 * @param wizardActiveItem {wizardItemInterface}
 * @param wizardSelectedValues {object}
 * @param returnAllSteps {boolean}
 * @param additionalWizardValuesToCheck {object}
 * @returns {array}
 */
export const getAvailableSteps = (wizardActiveItem, wizardSelectedValues, returnAllSteps, additionalWizardValuesToCheck = null) => {
  if (!wizardActiveItem || !wizardActiveItem.wizardGroups || !wizardActiveItem.wizardEdges) {
    return []
  }

  const maxWizardSteps = 50;

  let stepsArray = [{
    stepIndex: 0,
    stepGroups: getCurrentGroupsFromSource("0", wizardActiveItem),
  }];

  for (let i = 1; i <= maxWizardSteps; i++) {
    const prevStepGroups = returnAllSteps && stepsArray[i - 1] ? stepsArray[i - 1].stepGroups : [];
    const currentGroups = getCurrentGroups(wizardActiveItem, i, wizardSelectedValues, prevStepGroups, additionalWizardValuesToCheck)
    if (!currentGroups.length) {
      break;
    }
    stepsArray.push({
      stepIndex: i,
      stepGroups: currentGroups
    })
  }

  return stepsArray
}


/**
 * this returns new wizard steps depending on the current selection
 * @param wizardActiveItem {wizardItemInterface}
 * @param currentStep {number}
 * @param allWizardSelectedValues {object}
 * @returns {object}
 */
export const getNextWizardSteps = (wizardActiveItem, currentStep, allWizardSelectedValues) => {
// const oldWizardSteps = getAvailableSteps(wizardActiveItem, oldWizardSelectedValues, true);

  let newValues = {};
  Object.keys(allWizardSelectedValues).forEach(key => {
    if (+key <= currentStep) {
      newValues[key] = allWizardSelectedValues[key] // here we create values object just until the current selected value
    }
  });

  const afterCurrentSelectionWizardSteps = getAvailableSteps(wizardActiveItem, newValues, true, allWizardSelectedValues);
  const allWizardSteps = getAvailableSteps(wizardActiveItem, allWizardSelectedValues, true);

  const nextStep = currentStep + 1;

  if (
    afterCurrentSelectionWizardSteps[nextStep] && allWizardSteps[nextStep]
    && afterCurrentSelectionWizardSteps[nextStep].stepGroups.every((wizardGroup, wizardGroupIndex) => {
      return allWizardSteps[nextStep].stepGroups[wizardGroupIndex] && wizardGroup.id === allWizardSteps[nextStep].stepGroups[wizardGroupIndex].id
    }) && (allWizardSelectedValues[nextStep] && allWizardSelectedValues[nextStep].wizardGroup
      && afterCurrentSelectionWizardSteps[nextStep].stepGroups.some(wizG => allWizardSelectedValues[nextStep].wizardGroup.id === wizG.id))
  ) {
    return allWizardSteps // keep it, as we didn't change the flow
  }

  return afterCurrentSelectionWizardSteps // use new ones, as the flow was changed

}


/**
 * this returns conflicting with the current selections steps
 * @param allWizardSelectedValues {object}
 * @param actionPayload {object} // {wizardActiveItem, quoteIndex}
 * @returns {array}
 */
export const getConflictingWizardSteps = (allWizardSelectedValues, actionPayload) => {

  const {wizardActiveItem} = actionPayload;

  const currentStep = +Object.keys(actionPayload.wizardSelectedValues)[0];

  let conflictingWizardSteps = [];
  const newWizardSteps = getNextWizardSteps(wizardActiveItem, currentStep, allWizardSelectedValues);

  Object.keys(allWizardSelectedValues).forEach(stepIndex => {
    if (allWizardSelectedValues[stepIndex] && allWizardSelectedValues[stepIndex].wizardGroup && (!newWizardSteps[stepIndex]
      || !newWizardSteps[stepIndex].stepGroups.some(wizardGroup => wizardGroup.id === allWizardSelectedValues[stepIndex].wizardGroup.id))) {
      conflictingWizardSteps.push({
        stepIndex,
        wizardGroupId: allWizardSelectedValues[stepIndex].wizardGroup.id,
      });
    }
  })

  // console.log("newWizardSteps", newWizardSteps);
  // console.log("allWizardSelectedValues", allWizardSelectedValues);
  // console.log("conflictingWizardSteps", conflictingWizardSteps);

  return conflictingWizardSteps
}


export const getUpdatedAllWizardValues = (state, quoteIndex, wizardItemId, wizardSelectedValues) => {
  const newWizObject = {
    quoteIndex,
    wizardItemId,
    lastVisitedStep: state.wizardActiveStep,
    wizardSelectedValues: Object.assign({}, wizardSelectedValues)
  };
  const existingIndex = state.allWizardsValues.findIndex(wizSelObject => {
    return wizSelObject.wizardItemId === newWizObject.wizardItemId && wizSelObject.quoteIndex === newWizObject.quoteIndex
  });
  existingIndex > -1
    ? state.allWizardsValues.splice(existingIndex, 1, newWizObject)
    : state.allWizardsValues.push(newWizObject);

  state.allWizardsValues = state.allWizardsValues.map(wizObject => {
    if (wizObject.quoteIndex === quoteIndex) {
      wizObject.isLastActiveWizard = wizObject.wizardItemId === wizardItemId // to return back and force correctly
    }
    return wizObject
  });

  return [...state.allWizardsValues]
}
